import { makeToast } from '@cognite/cogs-lab';
import { Body, Checkbox, Flex, Modal } from '@cognite/cogs.js-v10';
import { LOCIZE_NAMESPACES, useTranslation } from '@infield/features/i18n';
import { useState } from 'react';

import * as S from './elements';

export type SaveConfigurationModalButtonArgs = {
  isDisabled: boolean;
  isLoading: boolean;
  onSave: () => Promise<void>;
  hasButtonHelpText?: boolean;
  extraContent?: React.ReactNode;
};

const SaveConfigurationModalButton: React.FC<
  SaveConfigurationModalButtonArgs
> = ({
  hasButtonHelpText = false,
  onSave,
  isLoading,
  isDisabled,
  extraContent,
}) => {
  const { t } = useTranslation(LOCIZE_NAMESPACES.configuration);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleOkClick = async () => {
    try {
      await onSave();
      makeToast({
        body: t('CONFIG_SAVE_TOAST_SUCCESS', 'Configuration saved'),
        type: 'success',
      });
      setShowConfirmationModal(false); // Close the modal after success
    } catch (error) {
      makeToast({
        body: t(
          'CONFIG_SAVE_TOAST_ERROR',
          'Couldn’t save configuration. Try again.'
        ),
        type: 'danger',
      });
    }
  };

  return (
    <Flex direction="column" gap={16}>
      {showConfirmationModal && (
        <Modal
          title={t(
            'CONFIRMATION_CONFIG_MODAL_TITLE',
            'Save configuration changes?'
          )}
          visible
          size="medium"
          onCancel={() => setShowConfirmationModal(false)}
          cancelText={t('CONFIRMATION_CONFIG_MODAL_CANCEL', 'Cancel')}
          onOk={handleOkClick}
          okText={t('CONFIRMATION_CONFIG_MODAL_OK', 'Save')}
          okDisabled={!confirmed}
        >
          <S.ExtraContent>{extraContent}</S.ExtraContent>
          <S.ConfirmationBody>
            {t(
              'CONFIRMATION_CONFIG_MODAL_BODY',
              'The change is permanent and will affect all users and the organization setup'
            )}
          </S.ConfirmationBody>
          <Checkbox
            checked={confirmed}
            onChange={() => setConfirmed(!confirmed)}
            label={t(
              'CONFIRMATION_CONFIG_MODAL_CHECKBOX',
              'I am authorized to make this change and have backed up all configuration data'
            )}
          />
        </Modal>
      )}
      <S.SaveButton
        data-testid="save-config-with-confirmation-button"
        onClick={() => setShowConfirmationModal(true)}
        disabled={isDisabled}
        loading={isLoading}
      >
        {t('SAVE_CONFIGURATION_BUTTON', 'Save configuration')}
      </S.SaveButton>
      {hasButtonHelpText && (
        <Body size="x-small">
          {t(
            'SAVE_CONFIG_BUTTON_HELP_TEXT',
            'Saving configuration will reload the app'
          )}
        </Body>
      )}
    </Flex>
  );
};

export default SaveConfigurationModalButton;
